import siteMessage from "@insite/client-framework/SiteMessage";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import setFieldValue from "@store/Store/Pages/CreateAccountForm/Handlers/SetFieldValue";
import useFieldValidation from "@store/Store/Pages/CreateAccountForm/Hooks/useFieldValidation";
import { emailsRegexp, phoneRegex } from "@store/Widgets/CreateAccount/CreateAccountForm";
import React, { ChangeEvent, FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}
export type FormTextFieldType = "text" | "email" | "phone" | "longText";

interface FieldsObj {
    [fields.label]: string;
    [fields.hintText]: string;
    [fields.isRequired]: boolean;
}

interface OwnProps {
    fields: FieldsObj;
    valueKey: string;
    value: string | undefined;
    type: FormTextFieldType;
}

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface FormTextFieldStyles {
    textField?: TextFieldPresentationProps;
    commentsTextArea?: TextAreaProps;
}

export const formTextFieldStyles: FormTextFieldStyles = {};

const styles = formTextFieldStyles;

const FormTextField: FC<Props> = ({ fields, valueKey, value, type, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const [errorMessage, setErrorMessage] = useState("");

    const validate = () => {
        const message =
            !value && isRequired
                ? siteMessage(siteMessage("Field_Required", label) as string)
                : !value ||
                  (type === "email" && emailsRegexp.test(value)) ||
                  !value ||
                  (type === "phone" && phoneRegex.test(value)) ||
                  type === "text" ||
                  type === "longText"
                ? ""
                : siteMessage(siteMessage("Field_Invalid", label) as string);

        setErrorMessage(message as string);
        return !message;
    };

    useFieldValidation(valueKey, value, validate);

    const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: valueKey, value: event.target.value });
    };
    const changeTextAreaHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setFieldValue({ key: valueKey, value: event.target.value });
    };

    if (type === "longText") {
        return (
            <TextArea
                {...styles.commentsTextArea}
                label={label}
                required={isRequired}
                placeholder={hintText}
                value={value || ""}
                error={errorMessage}
                onChange={changeTextAreaHandler}
            />
        );
    }
    return (
        <TextField
            {...styles.textField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={value || ""}
            error={errorMessage}
            onChange={changeHandler}
        />
    );
};
export default connect(null, mapDispatchToProps)(FormTextField);

export const formTextFieldFieldDefinitions = (
    labelDefaultValue: string,
    hintDefaultValue: string,
    requiredDefaultValue: boolean,
) => {
    return [
        {
            name: fields.label,
            displayName: "Label",
            editorTemplate: "TextField",
            fieldType: "Translatable",
            defaultValue: labelDefaultValue,
            isRequired: true,
        },
        {
            name: fields.hintText,
            displayName: "Hint Text",
            editorTemplate: "TextField",
            fieldType: "Translatable",
            defaultValue: hintDefaultValue,
            isRequired: true,
        },
        {
            name: fields.isRequired,
            displayName: "Required Field",
            editorTemplate: "CheckboxField",
            defaultValue: requiredDefaultValue,
            fieldType: "General",
        },
    ] as FieldDefinition[];
};
